import { Button, IconButton } from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/shared/ui/atoms';
import { useEnrollmentDocumentDownload } from '../../../hooks/useEnrollmentDocumentDownload';
import type { EnrollmentDocument } from '../../../types/enrollment';

export interface EnrollmentDocumentViewerModalProps {
  readonly doc: EnrollmentDocument | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

function detectFileType(fileName: string): 'pdf' | 'image' | 'other' {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  return 'other';
}

function DocumentContent({ url, fileName }: { readonly url: string; readonly fileName: string }) {
  const { t } = useTranslation('personManagement');
  const fileType = detectFileType(fileName);

  if (fileType === 'pdf') {
    return <iframe src={url} title={fileName} className="h-full w-full rounded-xs border-0" />;
  }

  if (fileType === 'image') {
    return (
      <div className="flex h-full items-center justify-center overflow-auto bg-ctx-layout-body p-xs">
        <img
          src={url}
          alt={fileName}
          className="max-h-full max-w-full rounded-xs object-contain shadow-level1"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-xxs text-ctx-content-placeholder">
      <ExclamationCircleIcon className="h-[2rem] w-[2rem]" />
      <span className="ts-paragraph-xs">
        {t('enrollmentDocumentation.viewer.unsupportedFormat')}
      </span>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Button hierarchy="secondary" size="sm" leadingIcon={ArrowTopRightOnSquareIcon}>
          {t('enrollmentDocumentation.viewer.openInNewTab')}
        </Button>
      </a>
    </div>
  );
}

/**
 * Modal de visualização de documento de matrícula.
 *
 * Busca a URL assinada do documento via `useEnrollmentDocumentDownload`
 * apenas quando o modal está aberto, evitando buscas desnecessárias e
 * respeitando o TTL curto das URLs assinadas (`staleTime: 0`).
 *
 * @param doc - Documento a ser exibido; `null` mantém o modal inativo
 * @param open - Controla a visibilidade do modal
 * @param onClose - Callback chamado ao fechar
 */
export function EnrollmentDocumentViewerModal({
  doc,
  open,
  onClose,
}: EnrollmentDocumentViewerModalProps) {
  const { t } = useTranslation('personManagement');
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    data: signedUrl,
    isLoading,
    isError,
  } = useEnrollmentDocumentDownload(open && doc !== null ? doc.id : null);

  const handleDownload = async () => {
    if (!signedUrl || !doc) return;
    setIsDownloading(true);
    try {
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = doc.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={v => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 bg-base-neutral-black/intense" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex h-[90vh] w-[90vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level3 focus:outline-none">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-xxs">
            <div className="flex min-w-none flex-col gap-quark">
              <Dialog.Title className="ts-paragraph-xs !font-medium text-ctx-content-title">
                {t('enrollmentDocumentation.viewer.title')}
              </Dialog.Title>
              {doc && (
                <Dialog.Description className="ts-caption-xxs text-ctx-content-placeholder truncate">
                  {doc.type} · {doc.fileName}
                </Dialog.Description>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-quark">
              {signedUrl && (
                <>
                  <IconButton
                    icon={ArrowDownTrayIcon}
                    hierarchy="tertiary"
                    size="sm"
                    ariaLabel={t('actions.download')}
                    isLoading={isDownloading}
                    onClick={handleDownload}
                  />
                  <a href={signedUrl} target="_blank" rel="noopener noreferrer">
                    <IconButton
                      icon={ArrowTopRightOnSquareIcon}
                      hierarchy="tertiary"
                      size="sm"
                      ariaLabel={t('enrollmentDocumentation.viewer.openInNewTab')}
                    />
                  </a>
                </>
              )}
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.back')}
                onClick={onClose}
              />
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1">
            {isLoading && (
              <div className="flex h-full items-center justify-center gap-xxs text-ctx-content-placeholder">
                <Spinner size="md" />
                <span className="ts-paragraph-xs">
                  {t('enrollmentDocumentation.viewer.loading')}
                </span>
              </div>
            )}

            {isError && (
              <div className="flex h-full flex-col items-center justify-center gap-xxs text-ctx-content-placeholder">
                <ExclamationCircleIcon className="h-[2rem] w-[2rem]" />
                <span className="ts-paragraph-xs">{t('enrollmentDocumentation.viewer.error')}</span>
              </div>
            )}

            {!isLoading && !isError && signedUrl && doc && (
              <DocumentContent url={signedUrl} fileName={doc.fileName} />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
